import _ from 'lodash';
import React, { type FC, useEffect } from 'react';
import { hoc } from '#packages/util';
import withSendBi from '../hocs/withSendBi';
import {
  mapDispatchToProps,
  mapStateToProps,
  type LimitlessZoomStateProps,
  type ZoomDispatchProps,
} from './limitlessZoomMappers';
import { withRestrictions } from '#packages/editorRestrictions';
import { NumericInput } from '@wix/wix-base-ui';
import styles from './limitlessZoom.scss';
import { IconButton } from '@wix/wix-base-ui';
import { Add, Minus } from '@wix/wix-ui-icons-common/classic-editor';
import { Tooltip, ContextMenu } from '@wix/wix-base-ui';
import LimitlessZoomMenu from './limitlessZoomMenu';
import {
  getValidZoomPercentage,
  getIncreaseZoomPercentageBy10,
  getDecreasedZoomPercentageBy10,
  MAX_ZOOM,
  MIN_ZOOM,
} from './limitlessZoomUtils';

const {
  connect,
  STORES: { EDITOR_API },
} = hoc;

export interface LimitlessZoomOwnProps {
  disabled?: boolean;
  sendBi: () => void;
}

interface LimitlessZoomProps
  extends LimitlessZoomOwnProps,
    ZoomDispatchProps,
    LimitlessZoomStateProps {}

const LimitlessZoom: FC<LimitlessZoomProps> = ({
  enterZoomMode,
  disabled,
  siteScale,
}) => {
  const [zoomPercentageInput, setZoomPercentageInput] = React.useState(
    siteScale * 100,
  );
  const currentZoomPercentage = siteScale * 100;

  useEffect(() => {
    if (disabled) {
      updateZoomPercentage(100);
    }
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setZoomPercentageInput(siteScale * 100);
  }, [siteScale]);

  function updateZoomPercentage(requestedZoomPrecentage: number) {
    if (requestedZoomPrecentage === currentZoomPercentage) {
      return;
    }
    const newZoomPercentage = getValidZoomPercentage(requestedZoomPrecentage);

    enterZoomMode(newZoomPercentage / 100);
  }

  function decreaseZoomPercentageBy10() {
    updateZoomPercentage(getDecreasedZoomPercentageBy10(currentZoomPercentage));
  }

  function increaseZoomPercentageBy10() {
    updateZoomPercentage(getIncreaseZoomPercentageBy10(currentZoomPercentage));
  }

  return (
    <div className={styles.limitlessZoom}>
      <Tooltip
        content={'Topbar_limitless_zoom_zoomout_tooltip'}
        alignment="BOTTOM"
      >
        <IconButton
          size="medium"
          skin="light-dark"
          onClick={decreaseZoomPercentageBy10}
          automationId="limitless-zoom-minus-btn"
        >
          <Minus />
        </IconButton>
      </Tooltip>
      <ContextMenu
        alignment="BOTTOM"
        direction="MIDDLE"
        customButton={
          <NumericInput
            unit="percent"
            value={zoomPercentageInput}
            onChange={(value) => {
              setZoomPercentageInput(value);
              if (value === MIN_ZOOM) {
                // When the input is out of range, onBlur happens before onChange.
                updateZoomPercentage(MIN_ZOOM);
              }
              if (value === MAX_ZOOM) {
                updateZoomPercentage(MAX_ZOOM);
              }
            }}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            placeholder="placeholder"
            automationId="limitless-zoom-input"
            onBlur={() => {
              updateZoomPercentage(zoomPercentageInput);
            }}
          />
        }
      >
        <LimitlessZoomMenu
          updateZoomPercentage={updateZoomPercentage}
          increaseZoomPercentageBy10={increaseZoomPercentageBy10}
          decreaseZoomPercentageBy10={decreaseZoomPercentageBy10}
        />
      </ContextMenu>
      <Tooltip
        content="Topbar_limitless_zoom_zoomin_tooltip"
        alignment="BOTTOM"
      >
        <IconButton
          size="medium"
          skin="light-dark"
          onClick={increaseZoomPercentageBy10}
          automationId="limitless-zoom-plus-btn"
        >
          <Add />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const LimitlessZoomComponent = (props: LimitlessZoomProps) => {
  return <LimitlessZoom {...props} />;
};

const ConnectedLimitlessZoom = _.flow(
  withRestrictions('top-bar_zoom-out'),
  connect(EDITOR_API, mapStateToProps, mapDispatchToProps),
  withSendBi,
)(LimitlessZoomComponent);

ConnectedLimitlessZoom.pure = LimitlessZoomComponent;

export default ConnectedLimitlessZoom;
